import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { PushNotifications } from '@capacitor/push-notifications';

export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

/**
 * Sets native chrome (status bar style, safe-area class) and hides the
 * splash screen once the web app has mounted. No-op on web.
 */
export const initNativeShell = async (isDark) => {
  if (!isNative()) return;

  document.documentElement.classList.add('capacitor-native', `capacitor-${getPlatform()}`);

  try {
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    if (getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: isDark ? '#000000' : '#ffffff' });
    }
  } catch (e) {
    // StatusBar plugin can be unavailable on some devices/configs; not fatal.
  }

  try {
    await SplashScreen.hide();
  } catch (e) {
    // ignore
  }
};

export const hapticImpact = (style = 'light') => {
  if (!isNative()) return;
  const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
  Haptics.impact({ style: map[style] || ImpactStyle.Light }).catch(() => {});
};

export const hapticSuccess = () => {
  if (!isNative()) return;
  Haptics.notification({ type: NotificationType.Success }).catch(() => {});
};

export const hapticError = () => {
  if (!isNative()) return;
  Haptics.notification({ type: NotificationType.Error }).catch(() => {});
};

/**
 * Native share sheet. Returns true if handled natively; false means the
 * caller should fall back to its own (web) share UI.
 */
export const nativeShare = async ({ title, text, url }) => {
  if (!isNative()) return false;
  try {
    await Share.share({ title, text, url, dialogTitle: title });
    return true;
  } catch (e) {
    // User cancelled the share sheet - still "handled".
    return true;
  }
};

/**
 * Opens a URL. On native platforms this uses an in-app browser
 * (SFSafariViewController on iOS, Chrome Custom Tabs on Android) so the
 * app never behaves like a general-purpose web browser. On web it opens
 * a normal new tab.
 */
export const openExternal = async (url) => {
  if (!url) return;
  if (isNative()) {
    try {
      await Browser.open({ url });
      return;
    } catch (e) {
      // fall through to window.open
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Requests permission and registers this device for native push
 * notifications (APNs on iOS, FCM on Android). Calls `onToken` with the
 * device token once registration succeeds so the caller can send it to
 * the backend. No-op on web (use the existing VAPID web-push flow there).
 */
export const registerNativePush = async (onToken) => {
  if (!isNative()) return;

  try {
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') return;

    await PushNotifications.addListener('registration', (token) => {
      onToken?.(token.value);
    });
    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error:', err);
    });

    await PushNotifications.register();
  } catch (e) {
    console.error('Failed to register for native push:', e);
  }
};

/**
 * Native camera / photo library picker. Returns a File object so callers
 * can treat it exactly like a plain <input type="file"> selection. Returns
 * null if unavailable or cancelled, in which case the caller should fall
 * back to a regular file input on web.
 */
export const pickOrCapturePhoto = async ({ filename = 'photo.jpg' } = {}) => {
  if (!isNative()) return null;
  try {
    const photo = await Camera.getPhoto({
      resultType: CameraResultType.Uri,
      source: CameraSource.Prompt,
      quality: 85,
      promptLabelHeader: 'Add Photo',
      promptLabelPhoto: 'Choose from Library',
      promptLabelPicture: 'Take Photo',
    });
    const response = await fetch(photo.webPath);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type || 'image/jpeg' });
  } catch (e) {
    // User cancelled or permission denied.
    return null;
  }
};
